import { createReducer } from '@reduxjs/toolkit';
import {
  listEvents,
  searchEvents,
  filterEvents,
  exportEvents,
  getEventInfo,
  updateEventInfo,
  addEvent,
} from '../actions/event';

const initialState = {
  events: {
    data: [],            // List of events (includes filtered or search results)
    currentPage: 1,      // Current page for pagination
    totalRecords: 0,       // Total number of pages
    pageSize: 10,        // Items per page
  },
  loading: false,        // Loading state for listing, filtering, searching, and exporting events
  error: null,           // General error state

  // Export state as an object
  export: {
    loading: false,      // Loading state for export action
    success: false,      // Tracks if export was successful
    error: null,         // Export-specific error
  },

  // States for event actions
  viewDetails: {
    data: null,
    error: null,
  },
  updateInfo: {
    loading: false,      // Loading state for update action
    success: false,      // Tracks if update was successful
    error: null,         // Update-specific error
  },
  addEvent: {
    loading: false,      // Loading state for add event action
    success: false,      // Tracks if add event was successful
    error: null,         // Add event-specific error
  },
};

const eventsReducer = createReducer(initialState, {
  // Listing events (with pagination)
  [listEvents.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [listEvents.resolved]: (state, action) => {
  
    const { 
      data: events, 
      current_page: currentPage, 
      next_page_url: nextPageUrl, 
      prev_page_url: prevPageUrl, 
      total: totalRecords, 
      per_page: pageSize 
    } = action.payload;


    state.loading = false;
    state.events = {
      data: events,
      currentPage,
      totalRecords, 
      pageSize,  
      nextPageUrl,
      prevPageUrl   
    };
    
  },
  [listEvents.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle listing error
  },

  // Filter events
  [filterEvents.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [filterEvents.resolved]: (state, action) => {
    const { events, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.events = {
      data: events,    // Set filtered events data
      currentPage,        // Update pagination after filtering
      totalRecords,
      pageSize,
    };
  },
  [filterEvents.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle filtering error
  },

  // Search events
  [searchEvents.trigger]: (state) => {
    state.loading = true;
    state.error = null;
  },
  [searchEvents.resolved]: (state, action) => {
    const { events, currentPage, totalRecords, pageSize } = action.payload;
    state.loading = false;
    state.events = {
      data: events,    // Set search results
      currentPage,        // Update pagination after search
      totalRecords,
      pageSize,
    };
  },
  [searchEvents.rejected]: (state, action) => {
    state.loading = false;
    state.error = action.payload; // Handle search error
  },

  // Export events
  [exportEvents.trigger]: (state) => {
    state.export.loading = true;  // Set export-specific loading
    state.export.success = false; // Reset success status
    state.export.error = null;    // Clear export errors
  },
  [exportEvents.resolved]: (state) => {
    state.export.loading = false; // Stop export loading
    state.export.success = true;  // Set export success flag
  },
  [exportEvents.rejected]: (state, action) => {
    state.export.loading = false; // Stop export loading
    state.export.success = false; // Mark export as unsuccessful
    state.export.error = action.payload; // Handle export error
  },

  // View event details
  [getEventInfo.trigger]: (state) => {
    state.viewDetails.error = null;
  },
  [getEventInfo.resolved]: (state, action) => {
    state.viewDetails.data = action.payload; // Set event details
  },
  [getEventInfo.rejected]: (state, action) => {
    state.viewDetails.error = action.payload; // Handle view details error
  },

  // Update event info
  [updateEventInfo.trigger]: (state) => {
    state.updateInfo.loading = true;  // Set update-specific loading
    state.updateInfo.success = false; // Reset success status
    state.updateInfo.error = null;    // Clear update errors
  },
  [updateEventInfo.resolved]: (state) => {
    state.updateInfo.loading = false; // Stop update loading
    state.updateInfo.success = true;  // Set update success flag
  },
  [updateEventInfo.rejected]: (state, action) => {
    state.updateInfo.loading = false; // Stop update loading
    state.updateInfo.success = false; // Mark update as unsuccessful
    state.updateInfo.error = action.payload; // Handle update error
  },

  // Add event
  [addEvent.trigger]: (state) => {
    state.addEvent.loading = true;  // Set add event-specific loading
    state.addEvent.success = false; // Reset success status
    state.addEvent.error = null;    // Clear add event errors
  },
  [addEvent.resolved]: (state) => {
    state.addEvent.loading = false; // Stop add event loading
    state.addEvent.success = true;  // Set add event success flag
  },
  [addEvent.rejected]: (state, action) => {
    state.addEvent.loading = false; // Stop add event loading
    state.addEvent.success = false; // Mark add event as unsuccessful
    state.addEvent.error = action.payload; // Handle add event error
  },
});

export default eventsReducer;